import { useMemo, useState } from "react";
import {
  FiChevronDown,
  FiChevronLeft,
  FiChevronRight,
  FiChevronUp,
  FiSearch,
  FiHelpCircle,
  FiMic,
} from "react-icons/fi";
import { LuSlidersHorizontal } from "react-icons/lu";
import styled from "styled-components";

import FlexBox from "../FlexBox";
import { Body2, Caption, H2, H6 } from "../Headings";
import { DAVYS_GREY, PLUM_PURPLE, SUCCESS_GREEN } from "../colors";

const HELP_CARDS = [
  {
    id: "help-centre",
    title: "Help Centre",
    description:
      "Guidance on effective prompting and best practices for using AI Chat.",
    icon: FiHelpCircle,
  },
  {
    id: "session-transcription",
    title: "Session Transcription Settings",
    description: "Directly open your transcription settings.",
    icon: FiMic,
  },
  {
    id: "customisation-settings",
    title: "Customisation Settings",
    description: "Directly open your AI customisation settings.",
    icon: LuSlidersHorizontal,
  },
];

export const SUGGESTED_HELP_QUESTIONS = [
  {
    question: "Can AI Chat answer questions about any client on my caseload?",
    answer:
      "It answers questions in the context of the client you have currently selected. You can change the client context at any time within the chat to switch to another client.",
  },
  {
    question: "What are the limitations of what AI Chat will answer?",
    answer:
      "AI Chat will not:\n\n- Access the internet or any external data source.\n- Make clinical diagnoses or provide treatment recommendations.\n- Provide information about clinicians' personal details, qualifications, or performance.\n- Generate creative content (stories, poems, etc.) unrelated to clinical work.\n- Answer questions where there is not enough information in the records — it will tell you directly when it lacks sufficient data.",
  },
  {
    question: "What clinical data does AI Chat have access to?",
    answer:
      "AI Chat uses a set of MCPs (Model Context Protocol integrations) to access:\n\n- Session notes (including private notes)\n- Intake reports and EHR data\n- Diagnoses and diagnostic history\n- Prescriptions and medication details\n- Booking and session history\n- Treatment plans\n- Clinical reports\n- Clinical rating scales (mGAF, CGI, NSP)\n- Provider tagging and analytics\n\nAll of this is scoped to the specific client you have selected in the chat.",
  },
];

const Container = styled(FlexBox)`
  width: 100%;
  height: 100%;
  flex-direction: column;
  overflow: hidden;
`;

const Header = styled(FlexBox)`
  justify-content: space-between;
  align-items: center;
  padding: 1.25rem 1rem;
  border-bottom: 1px solid ${DAVYS_GREY[300]};
`;

const BackButton = styled.button`
  border: none;
  background: transparent;
  display: flex;
  align-items: center;
  gap: 0.25rem;
  color: ${DAVYS_GREY[800]};
  cursor: pointer;
  padding: 0;
`;

const Spacer = styled.div`
  width: 4rem;
`;

const Content = styled(FlexBox)`
  flex-direction: column;
  gap: 1rem;
  padding: 1rem;
  overflow-y: auto;

  &::-webkit-scrollbar {
    display: none;
  }

  scrollbar-width: none;
  -ms-overflow-style: none;
`;

const SearchBar = styled(FlexBox)`
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem;
  border: 1px solid ${DAVYS_GREY[400]};
  border-radius: 0.75rem;

  input {
    border: none;
    outline: none;
    width: 100%;
    color: ${DAVYS_GREY[900]};
    font-size: 1rem;
  }
`;

const Card = styled.button`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  border: 1px solid ${DAVYS_GREY[400]};
  border-radius: 1rem;
  background: ${DAVYS_GREY[100]};
  padding: 1rem;
  cursor: pointer;
  text-align: left;

  &:hover {
    background: ${DAVYS_GREY[200]};
  }
`;

const CardContent = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  flex: 1;
`;

const CardIcon = styled.div`
  width: 1.75rem;
  height: 1.75rem;
  min-width: 1.75rem;

  display: flex;
  align-items: center;
  justify-content: center;

  border-radius: 0.5rem;
  background: ${PLUM_PURPLE[700]};
  color: white;
`;

const CardText = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.025rem;
  flex: 1;
`;

const StatusChip = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 999px;
  padding: 0.25rem 0.75rem;
  min-height: 1.5rem;
  font-size: 0.75rem;
  font-weight: 700;
  color: ${({ $isEnabled }) =>
    $isEnabled ? SUCCESS_GREEN[800] : DAVYS_GREY[700]};
  background: ${({ $isEnabled }) =>
    $isEnabled ? SUCCESS_GREEN[100] : DAVYS_GREY[200]};
`;

const SuggestedItem = styled.button`
  width: 100%;
  border: none;
  border-top: 1px solid ${DAVYS_GREY[300]};
  padding: 1rem 0;
  background: transparent;
  display: flex;
  align-items: center;
  justify-content: space-between;
  color: ${DAVYS_GREY[800]};
  cursor: pointer;
  text-align: left;

  &:hover {
    color: ${PLUM_PURPLE[800]};
  }
`;

const AccordionAnswer = styled.div`
  padding: 0 0 1rem 0;
  border-top: none;
  white-space: pre-line;
`;

const AiChatHelpView = ({
  onBack,
  onOpenHelpCentre,
  onOpenTranscriptionSettings,
  onOpenCustomisationSettings,
  isTranscriptionEnabled,
}) => {
  const [search, setSearch] = useState("");
  const [openIndex, setOpenIndex] = useState(null);

  const visibleQuestions = useMemo(
    () =>
      SUGGESTED_HELP_QUESTIONS.filter(({ question }) =>
        question.toLowerCase().includes(search.toLowerCase())
      ),
    [search]
  );

  const handleCardClick = id => {
    if (id === "help-centre") {
      onOpenHelpCentre();
      return;
    }

    if (id === "session-transcription") {
      onOpenTranscriptionSettings();
      return;
    }

    onOpenCustomisationSettings();
  };

  return (
    <Container>
      <Header>
        <BackButton onClick={onBack}>
          <FiChevronLeft size={20} />
          <Body2>Back</Body2>
        </BackButton>

        <H2 bold>Help</H2>

        <Spacer />
      </Header>

      <Content>
        <SearchBar>
          <FiSearch size={20} color={DAVYS_GREY[600]} />
          <input
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Search help"
          />
        </SearchBar>

        {HELP_CARDS.map(({ id, title, description, icon: Icon }) => (
          <Card key={id} onClick={() => handleCardClick(id)}>
            <CardContent>
              <CardIcon>
                <Icon size={16} />
              </CardIcon>

              <CardText>
                <Body2 bold>{title}</Body2>

                {id === "session-transcription" ? (
                  <FlexBox align="center" columnGap="0.5rem">
                    <Body2 color={DAVYS_GREY[700]}>Current Status:</Body2>
                    <StatusChip $isEnabled={isTranscriptionEnabled}>
                      {isTranscriptionEnabled ? "ON" : "OFF"}
                    </StatusChip>
                  </FlexBox>
                ) : (
                  <Body2 color={DAVYS_GREY[700]}>{description}</Body2>
                )}
              </CardText>
            </CardContent>

            <FiChevronRight size={20} color={DAVYS_GREY[700]} />
          </Card>
        ))}

        <Caption
          spacing="8%"
          textTransform="uppercase"
          bold
          color={DAVYS_GREY[700]}
          margin="0.5rem 0 0"
        >
          Suggested Help
        </Caption>

        <FlexBox column>
          {visibleQuestions.map(({ question, answer }, index) => {
            const isOpen = openIndex === index;
            return (
              <div key={question}>
                <SuggestedItem
                  onClick={() => setOpenIndex(isOpen ? null : index)}
                >
                  <H6>{question}</H6>
                  {isOpen ? (
                    <FiChevronUp size={20} color={DAVYS_GREY[700]} />
                  ) : (
                    <FiChevronDown size={20} color={DAVYS_GREY[700]} />
                  )}
                </SuggestedItem>
                {isOpen && (
                  <AccordionAnswer>
                    <H6 color={DAVYS_GREY[700]}>{answer}</H6>
                  </AccordionAnswer>
                )}
              </div>
            );
          })}
        </FlexBox>
      </Content>
    </Container>
  );
};

export default AiChatHelpView;
